import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, Pin, Plane, MapPin, Users, Calendar, Loader2, Share2, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { MarkdownPreview } from '@/components/notes/MarkdownPreview';
import { notesApi } from '@/services/notesApi';
import { useToast } from '@/components/ui/use-toast';
import type { Note } from '@/types/notes.types';

export default function NoteDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const [note, setNote] = useState<Note | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    notesApi
      .getNote(Number(id))
      .then((res) => setNote(res.data.data))
      .catch(() => {
        toast({ title: '获取笔记失败', variant: 'destructive' });
        navigate('/notes');
      })
      .finally(() => setLoading(false));
  }, [id, navigate, toast]);

  const handleDelete = async () => {
    if (!note) return;
    if (!confirm('确定删除这篇笔记？')) return;
    try {
      await notesApi.deleteNote(note.id);
      toast({ title: '已删除' });
      navigate('/notes');
    } catch {
      toast({ title: '删除失败', variant: 'destructive' });
    }
  };

  const handlePin = async () => {
    if (!note) return;
    try {
      const res = await notesApi.pinNote(note.id);
      setNote({ ...note, isPinned: res.data.data.isPinned });
      toast({ title: res.data.data.isPinned ? '已置顶' : '已取消置顶' });
    } catch {
      toast({ title: '操作失败', variant: 'destructive' });
    }
  };

  const handleShareToggle = async () => {
    if (!note) return;
    if (!note.isShared) {
      const ok = confirm(
        '分享后所有用户可在广场看到此笔记内容，机组人员信息将自动隐藏。请检查是否包含个人敏感信息。确认分享？'
      );
      if (!ok) return;
    }
    try {
      const res = await notesApi.shareToggle(note.id);
      setNote({
        ...note,
        isShared: res.data.data.isShared,
        isAnonymous: res.data.data.isAnonymous,
      });
      toast({ title: res.data.message });
    } catch {
      toast({ title: '操作失败', variant: 'destructive' });
    }
  };

  const handleAnonymousToggle = async () => {
    if (!note) return;
    try {
      const res = await notesApi.updateNote(note.id, {
        title: note.title,
        content: note.content,
        isShared: note.isShared,
        isAnonymous: !note.isAnonymous,
      });
      setNote({ ...note, isAnonymous: res.data.data.isAnonymous });
      toast({ title: res.data.data.isAnonymous ? '已切换为匿名' : '已显示作者' });
    } catch {
      toast({ title: '操作失败', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!note) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">笔记不存在</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/notes')}>
          返回列表
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* 顶部栏 */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/notes')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">返回列表</span>
        </button>
        <div className="flex items-center gap-2">
          {note.isShared && (
            <div className="flex items-center gap-1.5 mr-2 text-xs text-gray-500">
              <Switch checked={note.isAnonymous} onCheckedChange={handleAnonymousToggle} />
              <span>匿名</span>
            </div>
          )}
          <Button
            variant={note.isShared ? 'default' : 'outline'}
            size="sm"
            onClick={handleShareToggle}
            className={`gap-1.5 ${note.isShared ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
          >
            {note.isShared ? <Globe className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            {note.isShared ? '已分享' : '分享'}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePin} className="gap-1.5">
            <Pin className={`w-4 h-4 ${note.isPinned ? 'fill-amber-500 text-amber-500' : ''}`} />
            {note.isPinned ? '已置顶' : '置顶'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/notes/${note.id}/edit`)} className="gap-1.5">
            <Edit2 className="w-4 h-4" />
            编辑
          </Button>
          <Button variant="outline" size="sm" onClick={handleDelete} className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
            删除
          </Button>
        </div>
      </div>

      {/* 标题 */}
      <h1 className="text-2xl font-bold text-gray-900 mb-4">{note.title}</h1>

      {/* 元信息 */}
      <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-gray-500">
        <span>更新于 {new Date(note.updatedAt).toLocaleString('zh-CN')}</span>
        {note.flightLogInfo && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded">
            <Calendar className="w-3 h-3" />
            {note.flightLogInfo.airNum} ({note.flightLogInfo.airDate})
          </span>
        )}
      </div>

      {/* 关联标签 */}
      <div className="flex flex-wrap gap-2 mb-6">
        {note.route && (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-lg border border-blue-100">
            <Plane className="w-3.5 h-3.5" />
            {note.route.displayName || note.route.codePair}
          </span>
        )}
        {note.airports?.map((ap) => (
          <span
            key={ap.iataCode}
            className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-sm rounded-lg border border-green-100"
          >
            <MapPin className="w-3.5 h-3.5" />
            {ap.iataCode} {ap.nameZh}
          </span>
        ))}
        {note.crewMembers?.map((c) => (
          <span
            key={c.id}
            className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-lg border border-purple-100"
          >
            <Users className="w-3.5 h-3.5" />
            {c.displayName}
          </span>
        ))}
        {note.tags?.map((t) => (
          <span key={t} className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-lg">
            #{t}
          </span>
        ))}
      </div>

      {/* 正文 */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <MarkdownPreview content={note.content} />
      </div>
    </div>
  );
}
